import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { downloadFile } from "reducers/Download";
import { API_URL, MODAL_TYPES } from "utils/constants";
import { BasicAction } from "widgets/ActionMenu";
import { selectedCreativeData } from "selectors/Selected";
import { openModal } from "reducers/Modal";
import { isDynamic } from "utils/common";
function useTaxonomyDownload(url, query = ""){
	const dispatch = useDispatch();
	const baseURL = `${API_URL}/crm/v1`;
	let downloadURL = `${baseURL}/${url}`;

	if (query) {
		downloadURL = `${downloadURL}?${query}`;
	}

	return () => { dispatch(downloadFile(downloadURL)); };
}

const IMPORT_CREATIVE_LABELS = {
	name: "Import Creative Labels",
	icon: "upload",
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const uploadModal = () => {
			dispatch(openModal(MODAL_TYPES.IMPORT_TAXONOMY));
		};

		return (
			<BasicAction
				name="Import Creative Labels"
				iconName="upload"
				handleClick={uploadModal}
			/>
		);
	},
};
const EXPORT_CREATIVE_LABELS = {
	name: "Export Creative Labels",
	icon: "download",
	component:({ dependentValues }) => {
		const { libraryId } = dependentValues;
		const url = `creative-libraries/${libraryId}/creatives/labels/export`;
		const handleClick = useTaxonomyDownload(url);

		return (
			<BasicAction
				name="Export Creative Labels"
				iconName="download"
				handleClick={handleClick}
			/>
		);
	},
};
const EXPORT_SELECTED_CREATIVE_LABELS = {
	name: "Export Creative Labels for Selected Creatives",
	icon: "download",
	component:({ dependentValues = {} }) => {
		const { libraryId } = dependentValues;
		const selectedCreatives = useSelector(selectedCreativeData);
		const classifiableCreatives = useMemo(() => {
			return selectedCreatives.filter(creative => !isDynamic(creative));
		}, [selectedCreatives]);

		const selectedIds = classifiableCreatives.map(creative => creative.id);
		const url = `creative-libraries/${libraryId}/creatives/labels/export`;
		const query = `creativesIds=${selectedIds}`;
		const handleClick = useTaxonomyDownload(url, query);
		let label = "Export Creative Labels for Selected Creatives";

		if (classifiableCreatives.length) {
			label = `Export Creative Labels for Selected Creatives (${classifiableCreatives.length})`;
		}

		return (
			<BasicAction
				name={label}
				iconName="download"
				handleClick={handleClick}
				isDisabled={!classifiableCreatives.length}
			/>
		);
	},
};

const IMPORT_VERSION_LABELS = {
	name: "Import Creative Labels",
	icon: "upload",
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const creativeId = useSelector(state => state.creativeDetails?.id);

		const openImportModal = () => {
			dispatch(openModal(MODAL_TYPES.IMPORT_TAXONOMY, { creativeId }));
		};

		return (
			<BasicAction
				name="Import Creative Labels"
				iconName="upload"
				handleClick={openImportModal}
			/>
		);
	},
};
const EXPORT_VERSION_LABELS = {
	name: "Export Creative Labels",
	icon: "download",
	component:({ dependentValues }) => {
		const { libraryId, creativeId } = dependentValues;

		const url = `creative-libraries/${libraryId}/creatives/${creativeId}/versions/labels/export`;
		const handleClick = useTaxonomyDownload(url);

		return (
			<BasicAction
				name="Export Creative Labels"
				iconName="download"
				handleClick={handleClick}
			/>
		);
	},
};
const EXPORT_SELECTED_VERSION_LABELS = {
	name: "Export Creative Labels for Selected Versions",
	icon: "download",
	component:({ dependentValues }) => {
		const { libraryId } = dependentValues;
		const selectedVersionIds = useSelector(state => state.selected.versions);
		const creativeId = useSelector(state => state.creativeDetails?.id);
		const versions = useSelector(state => state.creativeVersions?.items || []);

		const classifiableVersions = useMemo(() => {
			return versions.filter(version => selectedVersionIds.includes(version.id));
		}, [versions, selectedVersionIds]);

		const selectedClassifiableIds = classifiableVersions.map(version => version.id);
		const url = `creative-libraries/${libraryId}/creatives/${creativeId}/versions/labels/export`;
		const query = `versionIds=${selectedClassifiableIds}`;
		const handleClick = useTaxonomyDownload(url, query);
		let label = "Export Creative Labels for Selected Versions";

		if (classifiableVersions.length) {
			label = `Export Creative Labels for Selected Versions (${classifiableVersions.length})`;
		}

		return (
			<BasicAction
				name={label}
				iconName="download"
				handleClick={handleClick}
				isDisabled={!classifiableVersions.length}
			/>
		);
	},
};

/* Taxonomy realated actions for the Creative Grid's Import/Export Button */
export const TAXONOMY_IMPORT_EXPORT_ACTIONS_LIST = [
	IMPORT_CREATIVE_LABELS,
	EXPORT_CREATIVE_LABELS,
	EXPORT_SELECTED_CREATIVE_LABELS,
];

export const TAXONOMY_VERSIONS_IMPORT_EXPORT_ACTIONS_LIST = [
	IMPORT_VERSION_LABELS,
	EXPORT_VERSION_LABELS,
	EXPORT_SELECTED_VERSION_LABELS,
];

export const PRIVATE = {
	IMPORT_CREATIVE_LABELS,
	EXPORT_CREATIVE_LABELS,
	EXPORT_SELECTED_CREATIVE_LABELS,
	IMPORT_VERSION_LABELS,
	EXPORT_VERSION_LABELS,
	EXPORT_SELECTED_VERSION_LABELS,
};
